import { FC, useMemo, useState, useRef, useCallback, useId, useEffect } from 'react';
import classNames from 'classnames';
import styles from './linechart.module.sass';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip';
import type { ChartTooltipRow } from '../ChartTooltip/ChartTooltip';

// viewBox matches the container's CSS pixels (1 SVG unit = 1px) so text
// renders crisp, including on Retina.

export interface LineChartSeries {
  label: string;
  /** A null (or any non-finite value) is a gap, not a zero: the point is
   *  dropped from the domain, the polyline and the hover. */
  data: (number | null)[];
  color?: string;
  dashed?: boolean;
}

export interface LineChartProps {
  series: LineChartSeries[];
  labels: string[];
  height?: number;
  showGrid?: boolean;
  showDots?: boolean;
  showLegend?: boolean;
  showArea?: boolean;
  /** When true, render the value of each point inline above the line.
   *  Density-aware: skips labels following the same xStep that thins the
   *  x-axis tick labels, so dense series don't print on top of each other. */
  showValueLabels?: boolean;
  /** When true, render translucent vertical bars from the y=0 baseline up to
   *  each point of the first series, behind the line(s). */
  showBars?: boolean;
  /** Bars on their own scale, drawn behind the lines, with a right-hand axis.
   *  The mirror of `secondaryLine` on VerticalBarChart: for a magnitude in
   *  different units (volume under rates, count under percentages), where
   *  `showBars` (same y-scale, first series only) would be meaningless. */
  secondaryBars?: SecondaryBarsConfig;
  yDomain?: [number, number];
  ySuffix?: string;
  className?: string;
}

export interface SecondaryBarsConfig {
  values: number[];
  label: string;
  color?: string;
  /** Right-axis tick formatter. Defaults to the same compact K/M/B form the
   *  left axis uses. */
  formatTick?: (v: number) => string;
  /** Tooltip formatter, when the axis form is too short to be read as money.
   *  Falls back to `formatTick`. */
  formatValue?: (v: number) => string;
}

import { getChartPalette } from '@/lib/chartColors';

const fmtY = (v: number, suffix?: string): string => {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  let s: string;
  if (abs >= 1e9) {
    const n = abs / 1e9;
    s = `${sign}${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}B`;
  } else if (abs >= 1e6) {
    const n = abs / 1e6;
    s = `${sign}${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}M`;
  } else if (abs >= 1e3) {
    const n = abs / 1e3;
    s = `${sign}${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}K`;
  } else if (abs === 0) {
    s = '0';
  } else {
    s = `${sign}${abs % 1 === 0 ? String(abs) : abs.toFixed(2)}`;
  }
  return `${s}${suffix || ''}`;
};

export const niceTicks = (min: number, max: number, count = 5): number[] => {
  const range = max - min || 1;
  const rawStep = range / count;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  let step: number;
  if (norm <= 1.5) step = mag;
  else if (norm <= 3) step = 2 * mag;
  else if (norm <= 7) step = 5 * mag;
  else step = 10 * mag;
  // Both ends round outward onto the step, so the top tick is >= max. Stopping at
  // the last tick <= max would draw the highest points above the axis.
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let t = start; t <= end + step * 0.01; t += step) ticks.push(Math.round(t * 1e6) / 1e6);
  return ticks;
};

const smoothPath = (pts: { x: number; y: number }[]): string => {
  if (!pts.length) return '';
  if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];
    d += ` C${p1.x + (p2.x - p0.x) / 6},${p1.y + (p2.y - p0.y) / 6} ${p2.x - (p3.x - p1.x) / 6},${p2.y - (p3.y - p1.y) / 6} ${p2.x},${p2.y}`;
  }
  return d;
};

export const LineChart: FC<LineChartProps> = ({
  series,
  labels,
  height = 240,
  showGrid = true,
  showDots = true,
  showLegend = true,
  showArea = true,
  showValueLabels = false,
  showBars = false,
  secondaryBars,
  yDomain,
  ySuffix,
  className,
}) => {
  const uid = useId().replace(/:/g, '');
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [hIdx, setHIdx] = useState<number | null>(null);
  const [tipX, setTipX] = useState(0);
  const [tipY, setTipY] = useState(0);

  // Measure the container for a 1:1 viewBox
  const [W, setW] = useState(600);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = Math.round(el.clientWidth);
      if (w > 0) setW(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const H = height;
  // The right gutter widens to hold the secondary axis labels; without it the
  // right-hand ticks print over the last bar.
  const pad = { top: 16, right: secondaryBars ? 46 : 14, bottom: 24, left: 52 };
  const cw = W - pad.left - pad.right;
  const ch = H - pad.top - pad.bottom;

  const PALETTE = useMemo(() => getChartPalette(), []);
  const colored = useMemo(
    () => series.map((s, i) => ({ ...s, _c: s.color || PALETTE[i % PALETTE.length] })),
    [series, PALETTE],
  );

  const { yMin, yMax, yTicks } = useMemo(() => {
    if (yDomain) {
      const t = niceTicks(yDomain[0], yDomain[1]);
      return { yMin: t[0], yMax: t[t.length - 1], yTicks: t };
    }
    const all = series.flatMap(s => s.data).filter((v): v is number => v != null && isFinite(v));
    if (!all.length) return { yMin: 0, yMax: 1, yTicks: [0, 1] };
    const lo = Math.min(...all);
    const hi = Math.max(...all);
    const r = hi - lo || hi * 0.1 || 1;
    const min = lo >= 0 && lo - r * 0.05 < 0 ? 0 : lo - r * 0.05;
    const t = niceTicks(min, hi + r * 0.25);
    return { yMin: t[0], yMax: t[t.length - 1], yTicks: t };
  }, [series, yDomain]);

  const yR = yMax - yMin || 1;

  // Secondary (right) axis for `secondaryBars`, always anchored at zero: the bars
  // are a magnitude, and a non-zero baseline exaggerates small differences.
  const secTicks = useMemo(() => {
    if (!secondaryBars) return [];
    const max = Math.max(...secondaryBars.values.filter((v) => Number.isFinite(v)), 0);
    return niceTicks(0, max || 1);
  }, [secondaryBars]);
  const secMax = secTicks[secTicks.length - 1] || 1;
  const secFmt = secondaryBars?.formatTick ?? ((v: number) => fmtY(v));

  const xPos = useMemo(() => {
    const n = labels.length;
    if (n <= 1) return labels.map(() => pad.left + cw / 2);
    if (showBars || secondaryBars) {
      // Band-style x: each point sits in the centre of its own band, leaving
      // half a band of margin at both ends so the first/last bar fits fully
      // inside the plot instead of being half-clipped against the axis.
      const band = cw / n;
      return labels.map((_, i) => pad.left + band * (i + 0.5));
    }
    // Edge-to-edge for line-only charts (first point on the axis, last on the
    // right edge) — gives the line its full horizontal span.
    return labels.map((_, i) => pad.left + (i / (n - 1)) * cw);
  }, [labels, cw, pad.left, showBars, secondaryBars]);

  const pts = useMemo(
    () => colored.map(s =>
      s.data.map((v, i) => ({
        x: xPos[i],
        // A gap keeps its slot on the x-axis and gets a non-finite y, which is
        // what every consumer below already filters on to break the line.
        y: v == null ? NaN : pad.top + (1 - (v - yMin) / yR) * ch,
        v,
      })),
    ),
    [colored, xPos, ch, yMin, yR, pad.top],
  );

  const tipRows: ChartTooltipRow[] = useMemo(() => {
    if (hIdx === null) return [];
    const rows: ChartTooltipRow[] = colored
      .map(s => ({
        color: s._c,
        label: s.label,
        value: s.data[hIdx] == null ? undefined : fmtY(s.data[hIdx] as number, ySuffix),
      }))
      .filter((r): r is ChartTooltipRow => r.value !== undefined);
    // The bars carry the magnitude the rates are shares of, so the tooltip shows
    // both; with only the right axis the reader would estimate bar heights.
    const secV = secondaryBars?.values[hIdx];
    if (secondaryBars && secV != null && Number.isFinite(secV)) {
      const fmt = secondaryBars.formatValue ?? secondaryBars.formatTick ?? ((v: number) => fmtY(v));
      rows.push({
        color: secondaryBars.color ?? 'var(--gt-text-muted)',
        label: secondaryBars.label,
        value: fmt(secV),
      });
    }
    return rows;
  }, [hIdx, colored, ySuffix, secondaryBars]);

  const onMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mx = e.clientX - rect.left;

    let best = 0, bestD = Infinity;
    for (let i = 0; i < xPos.length; i++) {
      const d = Math.abs(mx - xPos[i]);
      if (d < bestD) { bestD = d; best = i; }
    }

    setHIdx(best);
    setTipX(rect.left + xPos[best]);
    setTipY(rect.top + pad.top + 8);
  }, [xPos, pad.top]);

  const onLeave = useCallback(() => setHIdx(null), []);

  const hitW = labels.length > 1 ? cw / (labels.length - 1) : cw;
  const xStep = labels.length <= 8 ? 1 : labels.length <= 16 ? 2 : labels.length <= 30 ? 3 : Math.ceil(labels.length / 10);

  if (W <= 0) return <div ref={wrapRef} className={classNames(styles.wrap, className, `h-[${height}px]`)} />;

  return (
    <div ref={wrapRef} className={classNames(styles.wrap, className)}>
      <svg
        ref={svgRef}
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className={styles.svg}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        <defs>
          {colored.map((s, i) => (
            <linearGradient key={i} id={`${uid}g${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s._c} stopOpacity="0.12" />
              <stop offset="100%" stopColor={s._c} stopOpacity="0" />
            </linearGradient>
          ))}
          <clipPath id={`${uid}c`}>
            <rect x={pad.left} y={pad.top} width={cw} height={ch} />
          </clipPath>
        </defs>

        {showGrid && yTicks.map((t, i) => {
          const y = pad.top + (1 - (t - yMin) / yR) * ch;
          return (
            <g key={i}>
              <line x1={pad.left} y1={y} x2={W - pad.right} y2={y} className={styles.grid} />
              <text x={pad.left - 6} y={y} textAnchor="end" dominantBaseline="central" className={styles.axLbl}>
                {fmtY(t, ySuffix)}
              </text>
            </g>
          );
        })}

        {labels.map((lbl, i) => {
          if (i % xStep !== 0 && i !== labels.length - 1) return null;
          return (
            <text key={i} x={xPos[i]} y={H - 4} textAnchor="middle" className={styles.axLbl}>
              {lbl}
            </text>
          );
        })}

        {/* Right-hand axis for the secondary bars: outside the clip so its labels sit
           in the gutter, muted because it is context for the lines. */}
        {secondaryBars && secTicks.map((t, i) => (
          <text
            key={`sec-${i}`}
            x={W - pad.right + 6}
            y={pad.top + (1 - t / secMax) * ch + 3}
            className={styles.axLbl}
          >
            {secFmt(t)}
          </text>
        ))}

        <g clipPath={`url(#${uid}c)`}>
          {secondaryBars && (() => {
            // Volume behind the rates, on its own zero-anchored scale, drawn first so the
            // lines and their `showBars` fill sit on top.
            const n = labels.length;
            const step = n > 1 ? Math.abs(xPos[1] - xPos[0]) : cw;
            const barW = Math.max(2, step * 0.62);
            const color = secondaryBars.color ?? 'var(--gt-text-muted)';
            return (
              <g aria-hidden="true" opacity={0.22}>
                {secondaryBars.values.map((v, i) => {
                  if (v == null || !Number.isFinite(v) || v <= 0) return null;
                  const h = (v / secMax) * ch;
                  if (h <= 0) return null;
                  const bx0 = Math.max(pad.left, xPos[i] - barW / 2);
                  const bx1 = Math.min(W - pad.right, xPos[i] + barW / 2);
                  const bw = Math.max(0, bx1 - bx0);
                  if (bw <= 0) return null;
                  return (
                    <rect
                      key={i}
                      x={bx0}
                      y={pad.top + ch - h}
                      width={bw}
                      height={h}
                      fill={color}
                      rx={1}
                    />
                  );
                })}
              </g>
            );
          })()}
          {showBars && (() => {
            // Combo-chart bars, drawn before the lines so the line sits on top. Only the
            // first series gets bars. Baseline = y at value 0, clamped to the plot area.
            const firstSeries = colored[0];
            const p = pts[0];
            if (!firstSeries || !p?.length) return null;
            const step = p.length > 1 ? Math.abs(p[1].x - p[0].x) : cw;
            const barW = Math.max(2, step * 0.55);
            const zeroNormalised = (0 - yMin) / yR;
            const baselineYRaw = pad.top + (1 - zeroNormalised) * ch;
            const baselineY = Math.max(pad.top, Math.min(pad.top + ch, baselineYRaw));
            return (
              <g aria-hidden="true">
                {p.map((pt, pi) => {
                  const v = firstSeries.data[pi];
                  if (v == null || !Number.isFinite(v)) return null;
                  if (!Number.isFinite(pt.y)) return null;
                  const top = Math.min(pt.y, baselineY);
                  const h = Math.abs(pt.y - baselineY);
                  if (h <= 0) return null;
                  // Clamp the bar to the plot area so the first/last bar (centred
                  // on a point that sits exactly on the left/right edge) sits
                  // flush against the axis instead of being half-clipped.
                  const bx0 = Math.max(pad.left, pt.x - barW / 2);
                  const bx1 = Math.min(W - pad.right, pt.x + barW / 2);
                  const bw = Math.max(0, bx1 - bx0);
                  if (bw <= 0) return null;
                  return (
                    <rect
                      key={`bar-${pi}`}
                      x={bx0}
                      y={top}
                      width={bw}
                      height={h}
                      fill={firstSeries._c}
                      opacity={0.18}
                      rx={1}
                    />
                  );
                })}
              </g>
            );
          })()}
          {colored.map((s, si) => {
            const p = pts[si];
            if (!p?.length) return null;
            const pValid = p.filter(pt => Number.isFinite(pt.y));
            if (!pValid.length) return null;
            const d = smoothPath(pValid);
            return (
              <g key={si}>
                {showArea && si === 0 && (
                  <path
                    d={`${d} L${pValid[pValid.length - 1].x},${pad.top + ch} L${pValid[0].x},${pad.top + ch} Z`}
                    fill={`url(#${uid}g${si})`}
                    className={styles.area}
                  />
                )}
                <path
                  d={d}
                  fill="none"
                  stroke={s._c}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={s.dashed ? '5 3' : undefined}
                  className={styles.line}
                  style={{ animationDelay: `${si * 100}ms` }} // staggered delay — no Tailwind equivalent
                />
                {showDots && p.map((pt, pi) => {
                  if (!Number.isFinite(pt.y)) return null;
                  const active = hIdx === pi;
                  return (
                    <circle
                      key={pi}
                      cx={pt.x}
                      cy={pt.y}
                      r={active ? 3.5 : 1.8}
                      fill={active ? '#fff' : s._c}
                      stroke={active ? s._c : 'none'}
                      strokeWidth={active ? 1.5 : 0}
                      style={{
                        opacity: hIdx === null ? 0.6 : (active ? 1 : 0.15),
                        transition: 'all 120ms ease',
                      }}
                    />
                  );
                })}
                {/* Value labels are rendered unclipped after this group, so labels on
                   peaks/valleys or first/last points are not cut off by the clipPath. */}
              </g>
            );
          })}

          {hIdx !== null && (
            <line x1={xPos[hIdx]} y1={pad.top} x2={xPos[hIdx]} y2={pad.top + ch} className={styles.crosshair} />
          )}

          {xPos.map((x, i) => (
            <rect key={i} x={x - hitW / 2} y={pad.top} width={hitW} height={ch} fill="transparent" className="cursor-crosshair" />
          ))}
        </g>

        {/* Value labels, outside the plot clipPath. Edge-aware anchoring keeps the
           first/last labels inside the SVG horizontally; the y-clamp keeps a peak
           label inside the viewBox. */}
        {showValueLabels && colored.map((s, si) => {
          const p = pts[si];
          if (!p?.length) return null;
          return (
            <g key={`vlg-${si}`} aria-hidden="true">
              {p.map((pt, pi) => {
                if (pi % xStep !== 0 && pi !== p.length - 1) return null;
                const v = s.data[pi];
                if (v == null || !Number.isFinite(v)) return null;
                if (!Number.isFinite(pt.y)) return null;
                const dy = -8 - si * 12;
                const anchor = pi === 0 ? 'start' : pi === p.length - 1 ? 'end' : 'middle';
                const ly = Math.max(10, pt.y + dy);
                return (
                  <text
                    key={`vl-${si}-${pi}`}
                    x={pt.x}
                    y={ly}
                    textAnchor={anchor}
                    className={styles.valueLabel}
                    fill={s._c}
                  >
                    {fmtY(v, ySuffix)}
                  </text>
                );
              })}
            </g>
          );
        })}
      </svg>

      {hIdx !== null && (
        <ChartTooltip
          visible
          header={labels[hIdx]}
          rows={tipRows}
          x={tipX}
          y={tipY}
          anchorBottom
        />
      )}

      {showLegend && (
        <div className={styles.legend}>
          {colored.map((s, i) => (
            <div key={i} className={styles.lgItem}>
              <span className={styles.lgLine} style={{ background: s._c }} />
              <span className={styles.lgText}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

LineChart.displayName = 'LineChart';
export default LineChart;
